import React, { useEffect, useRef, useState } from 'react';
import { Button, QueryErrorAlert } from '@/shared/components';
import { PermissionGuard } from '@/features/auth';
import { useDerivativesByIds } from '@/features/vehiclesearch/hooks/useVehicleNameLookups';
import { isApiError } from '@/services/api';
import { usePlanVehicleRulesQuery } from '../hooks/usePlanVehicleRulesQuery';
import { usePlanVehicleRuleMutations } from '../hooks/usePlanVehicleRuleMutations';
import { batchResultMessage } from '../utils/vehicleRuleBatchCopy';
import type {
    PlanVehicleRule,
    PlanVehicleRuleBatchPayload,
    PlanVehicleRuleBatchResult,
    PlanVehicleRulePayload,
} from '../types/planVehicleRule.types';
import { PlanVehicleRuleDataTable } from './PlanVehicleRuleDataTable';
import { PlanVehicleRuleBatchForm } from './PlanVehicleRuleBatchForm';
import { PlanVehicleRuleForm } from './PlanVehicleRuleForm';
import { ConfirmActionModal } from '@shared/components';

interface PlanVehicleRulePanelProps {
    planId: string;
}

type FormState = { type: 'create' } | { type: 'edit'; rule: PlanVehicleRule } | null;

/** Focus target for the pencil of a given rule. Re-queried rather than held as an element
 *  reference because the table's cells remount whenever the panel re-renders. */
const findRulePencil = (ruleId: string) =>
    document.querySelector<HTMLElement>(`[data-rule-actions="${ruleId}"] button`);

export const PlanVehicleRulePanel: React.FC<PlanVehicleRulePanelProps> = ({ planId }) => {
    const [formState, setFormState] = useState<FormState>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [batchResult, setBatchResult] = useState<PlanVehicleRuleBatchResult | null>(null);
    const [isFormDirty, setIsFormDirty] = useState(false);
    const [pendingEdit, setPendingEdit] = useState<PlanVehicleRule | null>(null);
    const formContainerRef = useRef<HTMLDivElement>(null);
    const editHeadingRef = useRef<HTMLHeadingElement>(null);
    const restoreFocusRuleIdRef = useRef<string | null>(null);
    const declinedSwitchRuleIdRef = useRef<string | null>(null);

    const {
        data: rules = [],
        isLoading: isRulesLoading,
        isError: isRulesError,
        isFetching: isRulesFetching,
        refetch: refetchRules,
    } = usePlanVehicleRulesQuery(planId);
    const { createBatchMutation, updateMutation, deleteMutation } = usePlanVehicleRuleMutations(planId);

    // Same argument shape as PlanVehicleRuleDataTable.tsx so the React Query key matches
    // byte-for-byte and this reads the table's already-loaded cache entry rather than issuing a
    // second request (design "Design gap 2").
    const {
        data: derivatives,
        isPending: isDerivativesPending,
        isError: isDerivativesError,
        isFetching: isDerivativesFetching,
        refetch: refetchDerivatives,
    } = useDerivativesByIds(rules.map((r) => r.derivative_id));

    const editDerivative =
        formState?.type === 'edit' && formState.rule.derivative_id
            ? derivatives?.find((d) => d.id === formState.rule.derivative_id)
            : undefined;

    const needsDerivative = formState?.type === 'edit' && Boolean(formState.rule.derivative_id);

    // The form seeds once at mount, so a derivative rule must not mount before its model is
    // recoverable from the widened lookup (design "Design gap 3").
    const awaitingDerivative = needsDerivative && isDerivativesPending;
    // A failed lookup is not a settled one: mounting here would open the saved derivative absent
    // and disabled, with no error and no way to recover it. Offer an explicit retry instead.
    const derivativeLookupFailed = needsDerivative && isDerivativesError;

    // scrollIntoView moves the viewport but not focus, so the opened form would never be announced.
    // Focus the edit heading on open, and hand focus back to the row's pencil on close. Both run
    // after commit, so the freshly rendered nodes are the ones focused.
    useEffect(() => {
        if (formState) {
            formContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            if (formState.type === 'edit') editHeadingRef.current?.focus();
            return;
        }
        const ruleId = restoreFocusRuleIdRef.current;
        if (!ruleId) return;
        restoreFocusRuleIdRef.current = null;
        findRulePencil(ruleId)?.focus();
    }, [formState]);

    // Backstop for the shared Modal's restore: the table cell remounts while the dialog is open,
    // so the pencil it saved as opener is disconnected by close time and its restore no-ops.
    useEffect(() => {
        if (pendingEdit) return;
        const ruleId = declinedSwitchRuleIdRef.current;
        if (!ruleId) return;
        declinedSwitchRuleIdRef.current = null;
        findRulePencil(ruleId)?.focus();
    }, [pendingEdit]);

    const handleCreateBatch = async (payload: PlanVehicleRuleBatchPayload) => {
        try {
            setBatchResult(await createBatchMutation.mutateAsync(payload));
            setFormState(null);
        } catch {
            // Failure is surfaced reactively via createBatchMutation.isError below; swallow to
            // avoid an unhandled rejection.
        }
    };

    const createError = createBatchMutation.isError
        ? (isApiError(createBatchMutation.error) ? createBatchMutation.error.message : 'Failed to add vehicle rules.')
        : null;

    // Both handlers capture the form state they close over, so no row switch may outrun either one.
    const isSaving = createBatchMutation.isPending || updateMutation.isPending;

    const handleClose = () => {
        restoreFocusRuleIdRef.current = formState?.type === 'edit' ? formState.rule.id : null;
        setFormState(null);
        setFormError(null);
        setIsFormDirty(false);
    };

    const handleUpdate = (rule: PlanVehicleRule) => (payload: PlanVehicleRulePayload) => {
        updateMutation.mutate(
            { ruleId: rule.id, payload },
            {
                onSuccess: () => {
                    handleClose();
                },
                onError: (err) => {
                    setFormError(isApiError(err) ? err.message : 'Failed to update vehicle rule.');
                },
            },
        );
    };

    const openEdit = (rule: PlanVehicleRule) => {
        setFormError(null);
        setIsFormDirty(false);
        setFormState({ type: 'edit', rule });
    };

    const handleEditRequest = (rule: PlanVehicleRule) => {
        // Row actions are disabled while a save is in flight; this backstops that, because the
        // captured success handler would otherwise close whichever rule the switch had moved on to.
        if (isSaving) return;

        const isLeavingOpenForm =
            formState !== null && (formState.type === 'create' || formState.rule.id !== rule.id);

        // Opening a row remounts the surface, discarding an unsaved batch draft or unsaved row
        // edits with no warning. Hold the switch until the admin confirms.
        if (isLeavingOpenForm && isFormDirty) {
            declinedSwitchRuleIdRef.current = rule.id;
            setPendingEdit(rule);
            return;
        }
        openEdit(rule);
    };

    const handleConfirmSwitch = () => {
        // isSaving backstops the dialog's disabled confirm button: switching mid-save would leave
        // the in-flight save's captured onSuccess to close whichever rule the switch moved on to.
        if (!pendingEdit || isSaving) return;
        // The switch proceeds, so the edit heading claims focus via the formState effect above.
        declinedSwitchRuleIdRef.current = null;
        openEdit(pendingEdit);
        setPendingEdit(null);
    };

    return (
        <>
            <div className="space-y-6">
                {isRulesLoading ? (
                    <PlanVehicleRuleDataTable data={[]} onEdit={() => {}} onRemove={() => {}} isLoading />
                ) : isRulesError ? (
                    // A failed load must never read as zero rules: no rules means nothing is eligible,
                    // so the empty state would misreport the plan's coverage.
                    <QueryErrorAlert
                        message="Could not load vehicle rules. This is not the same as having none defined."
                        onRetry={() => { void refetchRules(); }}
                        isRetrying={isRulesFetching}
                    />
                ) : rules.length === 0 ? (
                    <p className="text-mp-muted text-sm">No vehicle rules defined.</p>
                ) : (
                    <PlanVehicleRuleDataTable
                        data={rules}
                        onEdit={handleEditRequest}
                        onRemove={(rule) => deleteMutation.mutate(rule.id)}
                        removingId={deleteMutation.isPending ? (deleteMutation.variables ?? null) : null}
                        disableActions={isSaving}
                    />
                )}

                <PermissionGuard permission={{ resource: 'smr', action: 'plan.edit' }}>
                    <div className="border-t border-mp-border pt-6 max-w-2xl">
                        {formState ? (
                            <div ref={formContainerRef}>
                                {formState.type === 'edit' && (
                                    <h3 ref={editHeadingRef} tabIndex={-1} className="font-semibold mb-2">
                                        Edit Vehicle Rule
                                    </h3>
                                )}
                                {awaitingDerivative ? (
                                    <p className="text-sm text-mp-muted">Loading derivative...</p>
                                ) : derivativeLookupFailed ? (
                                    <div className="space-y-3">
                                        <QueryErrorAlert
                                            message="Could not load this rule's derivative. The form cannot open until it loads."
                                            onRetry={() => { void refetchDerivatives(); }}
                                            isRetrying={isDerivativesFetching}
                                        />
                                        <Button size="sm" type="button" variant="neutral" appearance="outline" onClick={handleClose}>
                                            Cancel
                                        </Button>
                                    </div>
                                ) : formState.type === 'create' ? (
                                    <PlanVehicleRuleBatchForm
                                        key="create"
                                        onSubmit={handleCreateBatch}
                                        isLoading={createBatchMutation.isPending}
                                        error={createError}
                                        onCancel={handleClose}
                                        onDirtyChange={setIsFormDirty}
                                    />
                                ) : (
                                    <PlanVehicleRuleForm
                                        key={formState.rule.id}
                                        mode="edit"
                                        rule={formState.rule}
                                        derivative={editDerivative}
                                        onSubmit={handleUpdate(formState.rule)}
                                        isLoading={updateMutation.isPending}
                                        error={formError}
                                        onCancel={handleClose}
                                        onDirtyChange={setIsFormDirty}
                                    />
                                )}
                            </div>
                        ) : (
                            <div>
                                <h3 className="font-semibold mb-2">Add Vehicle Rules</h3>
                                {batchResult && (
                                    <p className="text-sm text-mp-success mb-2" role="status" data-testid="pvr-batch-result">
                                        {batchResultMessage(batchResult.data.length, batchResult.skipped)}
                                    </p>
                                )}
                                <div className="flex items-start gap-4 justify-between">
                                    <p className="text-sm text-mp-muted">
                                        Define which vehicles this plan applies to — from an any-vehicle catch-all or a
                                        broad make down to a specific derivative, with optional engine size, age,
                                        transmission, and fuel type dimensions. Selecting a derivative overrides all
                                        other dimensions. A rule with no fields set matches every vehicle. Tick several
                                        makes, transmissions or fuel types to add one rule per combination.
                                    </p>
                                    <Button
                                        size="sm"
                                        type="button"
                                        variant="primary"
                                        onClick={() => {
                                            setBatchResult(null);
                                            setFormError(null);
                                            // The mutation holds its failure until reset, so a fresh form would
                                            // otherwise mount showing the previous attempt's alert.
                                            createBatchMutation.reset();
                                            setFormState({ type: 'create' });
                                        }}
                                        className="whitespace-nowrap"
                                    >
                                        Add Rule
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </PermissionGuard>
            </div>

            <ConfirmActionModal
                isOpen={pendingEdit !== null}
                title="Discard unsaved changes?"
                message={
                    formState?.type === 'create'
                        ? 'The new vehicle rules have unsaved selections. Editing an existing rule will discard them.'
                        : 'This vehicle rule has unsaved changes. Editing a different rule will discard them.'
                }
                confirmLabel="Discard and edit"
                cancelLabel="Keep editing"
                confirmVariant="warning"
                isPending={isSaving}
                onConfirm={handleConfirmSwitch}
                onClose={() => setPendingEdit(null)}
            />
        </>
    );
};
